// client/src/components/plataforma/planosFranquias/PlansFranquiasFormPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFranquiaPlanById, useMutateFranquiaPlans, useSubscriptionsForFranquiaPlan } from '../../../hooks/useFranquiaPlans';
import { Loader2, Save, Package, Settings, Users, CreditCard, Ticket, Sparkles, Home } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import FabSaveButton from '../../common/FabSaveButton';

const PlansFranquiasFormPage = () => {
    const { id: planoId } = useParams();
    const navigate = useNavigate();
    const isEditing = !!planoId;

    const { data: planoData, isLoading: pageLoading } = useFranquiaPlanById(planoId);
    const { createMutation, updateMutation, isPending: formLoading } = useMutateFranquiaPlans();
    const { data: subscriptions = [], isLoading: subscriptionsLoading } = useSubscriptionsForFranquiaPlan(planoId);
    
    const [plano, setPlano] = useState({
        name: '', 
        description: '', 
        price: '', 
        isActive: true,
        trialPeriodDays: 0,
        isDefaultTrial: false,
        paymentMethods: ['boleto'],
        features: {
            maxAccounts: 10,
            matrizFeePercentage: 30,
            hasHomePersonalizada: false,
            hasGeracaoIA: false
        },
    });
    const [activeTab, setActiveTab] = useState('detalhes');
    
    useEffect(() => {
        if (isEditing && planoData) {
            setPlano({
                name: planoData.name || '',
                description: planoData.description || '',
                price: (planoData.price / 100).toFixed(2).replace('.', ','),
                isActive: planoData.isActive,
                trialPeriodDays: planoData.trialPeriodDays || 0,
                isDefaultTrial: planoData.isDefaultTrial || false,
                paymentMethods: planoData.paymentMethods || ['boleto'],
                features: {
                    maxAccounts: planoData.features?.maxAccounts ?? 10,
                    matrizFeePercentage: planoData.features?.matrizFeePercentage ?? 30,
                    hasHomePersonalizada: planoData.features?.hasHomePersonalizada ?? false,
                    hasGeracaoIA: planoData.features?.hasGeracaoIA ?? false
                }
            });
        }
    }, [planoData, isEditing]);

    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setPlano(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }, []);
    
    const handlePaymentMethodChange = useCallback((e) => {
        const { value, checked } = e.target;
        setPlano(prev => {
            const currentMethods = prev.paymentMethods || [];
            if (checked) {
                return { ...prev, paymentMethods: [...currentMethods, value] };
            } else {
                return { ...prev, paymentMethods: currentMethods.filter(method => method !== value) };
            }
        });
    }, []);

    const handleFeatureChange = useCallback((e) => {
        const { name, value } = e.target;
        setPlano(prev => ({
            ...prev,
            features: {
                ...prev.features,
                [name]: Number(value)
            }
        }));
    }, []);

    const handleFeatureToggle = useCallback((e) => {
        const { name, checked } = e.target;
        setPlano(prev => ({
            ...prev,
            features: {
                ...prev.features,
                [name]: checked
            }
        }));
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        const priceInCents = Math.round(parseFloat(plano.price.replace(',', '.')) * 100);
        const trialDays = Number(plano.trialPeriodDays) || 0;
        const dataToSend = { 
            ...plano, 
            price: priceInCents, 
            trialPeriodDays: trialDays 
        };
        
        if (isEditing) {
            updateMutation.mutate(
                { id: planoId, planData: dataToSend },
                { 
                    onSuccess: () => navigate('/plataforma/planos-franquias') 
                }
            );
        } else {
            createMutation.mutate(
                dataToSend,
                { 
                    onSuccess: () => navigate('/plataforma/planos-franquias') 
                }
            );
        }
    };

    if (pageLoading) return (
        <div className="flex justify-center items-center h-64">
            <Loader2 className="animate-spin text-primary" size={48} />
        </div>
    );

    return (
        <div className="page-management-container">
            <form onSubmit={handleSubmit}>
                <PageHeader
                    icon={<Package />}
                    title={isEditing ? `Editar Plano: ${plano.name}` : 'Novo Plano de Franquia'}
                    navBack={{ to: '/plataforma/planos-franquias' }}
                />
                
                <div className="tabs-nav mt-6">
                    <button 
                        type="button" 
                        className={`tab-button ${activeTab === 'detalhes' ? 'is-active' : ''}`} 
                        onClick={() => setActiveTab('detalhes')}
                    >
                        <Package size={16} /> Detalhes do Plano
                    </button>
                    <button 
                        type="button" 
                        className={`tab-button ${activeTab === 'limites' ? 'is-active' : ''}`} 
                        onClick={() => setActiveTab('limites')}
                    >
                        <Settings size={16} /> Funcionalidades e Limites
                    </button>
                    {isEditing && (
                        <button 
                            type="button" 
                            className={`tab-button ${activeTab === 'inscricoes' ? 'is-active' : ''}`} 
                            onClick={() => setActiveTab('inscricoes')}
                        >
                            <Users size={16} /> Inscrições ({subscriptions.length})
                        </button>
                    )}
                </div>
                
                <div className="content-panel mt-0 pt-8">
                    {activeTab === 'detalhes' && (
                        <fieldset className="form-section" disabled={isEditing ? updateMutation.isPending : createMutation.isPending}>
                            <legend className="form-section-title">Informações Básicas</legend>
                            <div className="form-grid-container grid-cols-1 md:grid-cols-3">
                                <div className="form-group">
                                    <label>Nome do Plano*</label>
                                    <input 
                                        type="text" 
                                        name="name" 
                                        value={plano.name} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                        required 
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Preço Mensal (R$)*</label>
                                    <input 
                                        type="text" 
                                        name="price" 
                                        value={plano.price} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                        placeholder="ex: 199,90" 
                                        required 
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Duração do Teste Grátis (dias)</label>
                                    <input 
                                        type="number" 
                                        name="trialPeriodDays" 
                                        value={plano.trialPeriodDays} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                        min="0" 
                                    />
                                </div>
                                <div className="form-group full-width md:col-span-3">
                                    <label>Descrição</label>
                                    <textarea 
                                        name="description" 
                                        value={plano.description} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                        rows="2"
                                    ></textarea>
                                </div>
                                <div className="form-group full-width md:col-span-3">
                                    <label>Métodos de Pagamento Permitidos</label>
                                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-2">
                                        <div className="form-group-checkbox items-center">
                                            <input 
                                                type="checkbox" 
                                                id="payment_credit_card" 
                                                value="credit_card" 
                                                checked={plano.paymentMethods.includes('credit_card')} 
                                                onChange={handlePaymentMethodChange} 
                                                className="form-checkbox" 
                                            />
                                            <label htmlFor="payment_credit_card" className="flex items-center gap-2">
                                                <CreditCard size={16} /> Cartão de Crédito
                                            </label>
                                        </div>
                                        <div className="form-group-checkbox items-center">
                                            <input 
                                                type="checkbox" 
                                                id="payment_boleto" 
                                                value="boleto" 
                                                checked={plano.paymentMethods.includes('boleto')} 
                                                onChange={handlePaymentMethodChange} 
                                                className="form-checkbox" 
                                            />
                                            <label htmlFor="payment_boleto" className="flex items-center gap-2">
                                                <Ticket size={16} /> Boleto
                                            </label>
                                        </div>
                                        <div className="form-group-checkbox items-center">
                                            <input 
                                                type="checkbox" 
                                                id="payment_pix" 
                                                value="pix" 
                                                checked={plano.paymentMethods.includes('pix')} 
                                                onChange={handlePaymentMethodChange} 
                                                className="form-checkbox" 
                                            />
                                            <label htmlFor="payment_pix" className="flex items-center gap-2">
                                                <Sparkles size={16} /> PIX
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="form-group-checkbox items-center full-width md:col-span-3 border-t pt-4 mt-2">
                                    <input 
                                        type="checkbox" 
                                        name="isDefaultTrial" 
                                        id="isDefaultTrial" 
                                        checked={plano.isDefaultTrial} 
                                        onChange={handleChange} 
                                        className="form-checkbox" 
                                    />
                                    <label htmlFor="isDefaultTrial">Plano padrão para novos testes de franquia</label>
                                    <p className="form-hint">
                                        Marcar esta opção fará com que este seja o plano usado para todas as novas franquias que iniciarem um período de teste.
                                    </p>
                                </div>
                                {isEditing && (
                                    <div className="form-group-checkbox items-center full-width md:col-span-3">
                                        <input 
                                            type="checkbox" 
                                            name="isActive" 
                                            id="isActive" 
                                            checked={plano.isActive} 
                                            onChange={handleChange} 
                                            className="form-checkbox" 
                                        />
                                        <label htmlFor="isActive">Plano Ativo</label>
                                        <p className="form-hint">Desmarcar impedirá novas assinaturas.</p>
                                    </div>
                                )}
                            </div>
                        </fieldset>
                    )}
                    
                    {activeTab === 'limites' && (
                        <fieldset className="form-section" disabled={isEditing ? updateMutation.isPending : createMutation.isPending}>
                            <legend className="form-section-title">Limites da Franquia</legend>
                            <div className="form-grid-container grid-cols-1 md:grid-cols-2">
                                <div className="form-group">
                                    <label>Máximo de Contas (-1 para ilimitado)</label>
                                    <input 
                                        type="number" 
                                        name="maxAccounts" 
                                        value={plano.features.maxAccounts} 
                                        onChange={handleFeatureChange} 
                                        className="form-input" 
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Taxa da Matriz (%)</label>
                                    <input
                                        type="number"
                                        name="matrizFeePercentage"
                                        value={plano.features.matrizFeePercentage}
                                        onChange={handleFeatureChange}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group-checkbox items-center full-width md:col-span-2 border-t pt-4 mt-2">
                                    <input
                                        type="checkbox"
                                        name="hasHomePersonalizada"
                                        id="hasHomePersonalizada"
                                        checked={plano.features.hasHomePersonalizada}
                                        onChange={handleFeatureToggle}
                                        className="form-checkbox"
                                    />
                                    <label htmlFor="hasHomePersonalizada" className="flex items-center gap-2 cursor-pointer">
                                        <Home size={16} /> Home Personalizada
                                    </label>
                                    <p className="form-hint">
                                        Libera a edição da home (navbar, seções, tema) pras franquias assinantes deste plano — sem isso, elas usam o template padrão da matriz.
                                    </p>
                                </div>
                                <div className="form-group-checkbox items-center full-width md:col-span-2">
                                    <input
                                        type="checkbox"
                                        name="hasGeracaoIA"
                                        id="hasGeracaoIA"
                                        checked={plano.features.hasGeracaoIA}
                                        onChange={handleFeatureToggle}
                                        className="form-checkbox"
                                    />
                                    <label htmlFor="hasGeracaoIA" className="flex items-center gap-2 cursor-pointer">
                                        <Sparkles size={16} /> Geração de Conteúdo por IA
                                    </label>
                                    <p className="form-hint">
                                        Libera os botões "Gerar com IA" (texto e imagem) pras franquias assinantes deste plano — cada geração tem custo real de API.
                                    </p>
                                </div>
                            </div>
                        </fieldset>
                    )}
                    
                    {activeTab === 'inscricoes' && isEditing && (
                        <div className="table-container">
                            <div className="table-wrapper">
                                <table className="styled-table">
                                    <thead>
                                        <tr>
                                            <th>Nome da Franquia</th>
                                            <th>Responsável</th>
                                            <th>Status da Assinatura</th>
                                            <th>Próxima Cobrança</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {subscriptionsLoading ? (
                                            <tr>
                                                <td colSpan="4" className="text-center p-4">
                                                    <Loader2 size={18} className="icon animate-spin mx-auto" />
                                                </td>
                                            </tr>
                                        ) : subscriptions.length > 0 ? (
                                            subscriptions.map(sub => (
                                                <tr key={sub._id}>
                                                    <td>{sub.franquia?.nome || 'N/A'}</td>
                                                    <td>{sub.franquia?.responsavel?.name || 'N/A'}</td>
                                                    <td>
                                                        <span className={`badge ${sub.status === 'paid' || sub.status === 'trialing' || sub.status === 'active' ? 'badge-success' : 'badge-warning'}`}>
                                                            {sub.status || 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {sub.trialEnd
                                                            ? `Teste termina em ${new Date(sub.trialEnd).toLocaleDateString('pt-BR')}`
                                                            : (sub.currentPeriodEnd
                                                                ? new Date(sub.currentPeriodEnd).toLocaleDateString('pt-BR')
                                                                : 'N/A')
                                                        }
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center p-8">
                                                    Nenhuma franquia a assinar este plano.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                <FabSaveButton loading={isEditing ? updateMutation.isPending : createMutation.isPending} label="Salvar" />
            </form>
        </div>
    );
};

export default PlansFranquiasFormPage;